import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageLayout } from '@/components/molecules/pageLayout';
import { MaxContent } from '@/styles/globalStyles';
import { useTranslation } from '@/hooks/useTranslation';
import { AppRouters } from '@/utils/constants/routers';
import { useAuth } from '@/hooks/useAuth';
import { fetchProjectAnalysisReportHtml } from '@/services/project-analysis/fetchProjectAnalysisReportHtml';
import type { ProjectAnalysisHtmlErrorKind } from '@/services/project-analysis/fetchProjectAnalysisReportHtml';
import { Text } from '@/components/atoms/text';
import { Skeleton } from '@/components/ui/skeleton';

export default function ProjectAnalysisViewerPage() {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { token } = useAuth();

  const [htmlDocument, setHtmlDocument] = useState<string | null>(null);
  const [errorKind, setErrorKind] = useState<ProjectAnalysisHtmlErrorKind | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!reportId) return;

    let cancelled = false;

    (async () => {
      setLoading(true);
      setErrorKind(null);
      setHtmlDocument(null);

      const result = await fetchProjectAnalysisReportHtml(reportId, token);
      if (cancelled) return;

      if (result.ok) {
        setHtmlDocument(result.html);
      } else {
        setErrorKind(result.kind);
      }
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [reportId, token]);

  const errorMessage = (kind: ProjectAnalysisHtmlErrorKind) => {
    switch (kind) {
      case 'forbidden':
        return t('page.projectAnalysis.projectAnalysis.forbidden');
      case 'not_found':
        return t('page.projectAnalysis.projectAnalysis.notFound');
      case 'unauthorized':
        return t('page.projectAnalysis.projectAnalysis.unauthorized');
      default:
        return t('page.projectAnalysis.projectAnalysis.errorLoad');
    }
  };

  return (
    <PageLayout.Root
      contentPadding={{ vertical: 2, horizontal: 2 }}
      header={
        <PageLayout.Background>
          <PageLayout.Header
            title={t('page.projectAnalysis.projectAnalysis.title')}
            onBack={() => navigate(AppRouters.projectAnalysis)}
          />
        </PageLayout.Background>
      }
    >
      <MaxContent
        className="gap-4"
        style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
      >
        {loading && (
          <Skeleton className="min-h-[12rem] w-full flex-1 rounded-md border border-slate-200" />
        )}

        {!loading && errorKind && (
          <Text variant="body2" sx={{ color: 'error.main' }}>
            {errorMessage(errorKind)}
          </Text>
        )}

        {!loading && htmlDocument !== null && (
          <iframe
            key={reportId}
            title={t('page.projectAnalysis.projectAnalysis.title')}
            srcDoc={htmlDocument}
            className="w-full min-h-0 flex-1 border border-slate-200 rounded-md bg-white"
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
          />
        )}
      </MaxContent>
    </PageLayout.Root>
  );
}
